import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
} from '@mui/material';
import { useMutation, gql } from '@apollo/client';
import { EditWidgetDialog, EditableWidget } from './EditWidgetDialog';
import { DeleteWidgetDialog } from './DeleteWidgetDialog';
import WidgetPreviewDialog from './WidgetPreviewDialog';
import { WidgetRow } from './WidgetRow';
import { SourceField } from '../types';
import { DashboardWidgetData } from '../../Dashboard/components/CustomChartWidget';
import { FieldDefinition } from '../../Dashboard/utils/widgetSeries';

const DELETE_WIDGET = gql`
  mutation DeleteDashboardWidget($id: ID!) {
    deleteDashboardWidget(id: $id)
  }
`;

interface WidgetListEditorProps {
  widgets: DashboardWidgetData[];
  sourceFields: SourceField[];
  previewFields?: Map<string, FieldDefinition>;
  onMutated?: () => void;
}

export const WidgetListEditor: React.FC<WidgetListEditorProps> = ({
  widgets,
  sourceFields,
  previewFields,
  onMutated,
}) => {
  const [localWidgets, setLocalWidgets] = useState<DashboardWidgetData[]>([]);

  const [deleteWidget] = useMutation(DELETE_WIDGET, {
    refetchQueries: ['GetDashboardWidgets', 'GetDashboardWidgetsAndSections'],
    onCompleted: () => onMutated?.(),
  });

  const [editing, setEditing] = useState<EditableWidget | null>(null);
  const [deleting, setDeleting] = useState<DashboardWidgetData | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [previewing, setPreviewing] = useState<DashboardWidgetData | null>(null);

  useEffect(() => {
    setLocalWidgets(widgets);
  }, [widgets]);

  const sorted = [...localWidgets].sort((a, b) => a.orderIndex - b.orderIndex);

  const handleConfirmDelete = () => {
    if (!deleting) return;
    setDeleteLoading(true);
    const prev = [...localWidgets];
    setLocalWidgets((prev) => prev.filter((w) => w.id !== deleting.id));
    setDeleting(null);
    deleteWidget({ variables: { id: deleting.id } })
      .catch(() => {
        setLocalWidgets(prev);
      })
      .finally(() => setDeleteLoading(false));
  };

  return (
    <Box>
      <EditWidgetDialog
        open={!!editing}
        widget={editing}
        sourceFields={sourceFields}
        onSaved={() => setEditing(null)}
        onCancel={() => setEditing(null)}
      />
      <DeleteWidgetDialog
        open={!!deleting}
        widgetTitle={deleting?.title || ''}
        loading={deleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
      <WidgetPreviewDialog
        open={!!previewing}
        widget={previewing}
        previewField={previewing ? (previewFields?.get(previewing.fieldId) ?? undefined) : undefined}
        onClose={() => setPreviewing(null)}
      />

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
        Library — All Widgets ({widgets.length})
      </Typography>
      <Stack spacing={1}>
        {sorted.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No widgets yet — create one from your custom fields above.
          </Typography>
        )}
        {sorted.map((w) => (
          <WidgetRow
            key={w.id}
            widget={w}
            sourceFields={sourceFields}
            onEdit={(widget) => setEditing({
              id: widget.id,
              title: widget.title,
              fieldId: widget.fieldId,
              chartType: widget.chartType,
              aggregation: widget.aggregation,
              layout: widget.layout,
            })}
            onDelete={setDeleting}
            onPreview={setPreviewing}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default WidgetListEditor;
